/**
 * WebGLHeatMapLayer — WebGL-powered wind heatmap overlay.
 *
 * SpecRef: ARCH-002
 * Reference: docs/decisions/ARCH-002-webgl-heatmap-migration.md
 *
 * Replaces the Canvas2D HeatMapLayer with a GPU-accelerated renderer.
 * Pan/zoom is a sub-ms uniform update via fragment shader instead of
 * a per-pixel CPU re-render.
 *
 * Recovery model (3 tiers, escalating):
 *   Tier 1 — debounced renderer recreate, gated on stable layout
 *            (handles Mac mini "Open as app" handoff and routine GL hiccups)
 *   Tier 2 — after MAX_CONSECUTIVE_BLANKS, force recreate ignoring debounce
 *            (handles cases where debounce keeps deferring while canvas stays blank)
 *   Tier 3 — after MAX_RECREATE_FAILURES, page reload
 *            (terminal: GPU/driver is fundamentally wedged)
 */
import { styled } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { reportThrottled } from '#shared';

import { useGeoMap } from '../../GeoMap';
import { GRID_HEIGHT, GRID_WIDTH, WindGlRenderer } from '../lib';
import { useWindData, useWindTheme } from '../model';

// ── Styled canvas ─────────────────────────────────────────────────────────────

const Root = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  willChange: 'transform',
  // Visual overlay only; let pointer events reach the globe canvas underneath.
  pointerEvents: 'none',
});

const StyledCanvas = styled('canvas')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  filter: 'blur(4px)',
  pointerEvents: 'none',
});

// Chrome's "Open as app" handoff fires zero-size resize/focus events for
// up to ~1s before settling. Recreate only after layout is stably valid.
// Must be >= WATCHDOG_INTERVAL_ACTIVE_MS, otherwise the watchdog can keep
// resetting the debounce while alpha stays 0.
const STABLE_RECOVERY_DELAY_MS = 1_000;

// Watchdog runs at active interval until first successful render or after
// any blank detection; otherwise drops to idle to save CPU.
const WATCHDOG_INTERVAL_ACTIVE_MS = 1_000;
const WATCHDOG_INTERVAL_IDLE_MS = 3_000;
const PROBE_GRID_SIZE = 8;

// Tier 2: force recreate after this many consecutive blank watchdog detections.
const MAX_CONSECUTIVE_BLANKS = 3;
// Tier 3: reload page after this many failed recreate attempts.
const MAX_RECREATE_FAILURES = 2;

const SENTRY_THROTTLE_MS = 60_000;
const TAGS = { component: 'WebGLHeatMapLayer' };

// ── Component ─────────────────────────────────────────────────────────────────

interface WebGLHeatMapLayerProps {
  /** Frame-switch interval in seconds; blend crossfade uses the same duration. */
  blendDurationSec?: number;
}

export const WebGLHeatMapLayer = ({
  blendDurationSec = 1,
}: WebGLHeatMapLayerProps = {}) => {
  const {
    projectionRef,
    bounds,
    projectionHash,
    onChangeEnd,
    onChangeStart,
    onChangeProgress,
    contextId,
  } = useGeoMap();

  const { windState, windParams, preload } = useWindData();
  const { theme, opacity, threshold } = useWindTheme();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rendererRef = useRef<WindGlRenderer | null>(null);
  const rafRef = useRef<number>(0);
  const blendRafRef = useRef<number>(0);
  const prevFhrRef = useRef<number>(-1);

  // Sticky flag: set on any invalid-layout phase, cleared on successful recovery.
  const sawInvalidSizeRef = useRef(false);
  const stableRecoveryTimerRef = useRef<number>(0);

  // Holds the latest scheduleStableRecovery so the in-flight setTimeout
  // closure can recurse without depending on its own variable name (lint
  // rule) and without holding a stale callback if useCallback re-creates it.
  const scheduleStableRecoveryRef = useRef<((reason: string) => void) | null>(
    null,
  );

  // Latest renderer settings. hardRecreateRenderer reads from this ref so
  // it doesn't depend on theme/opacity/threshold/maxWindSpeed in deps —
  // keeping its identity (and that of scheduleStableRecovery) stable across
  // settings changes, so the env-handler effect doesn't rebind listeners
  // on every legend opacity tweak.
  const settingsRef = useRef({
    theme,
    opacity,
    threshold,
    maxWindSpeed: windState.maxWindSpeed,
  });

  // Bumped on context restore or hard recreate — re-runs texture upload.
  const [restoreGeneration, setRestoreGeneration] = useState(0);

  // Layout validity gate. Touching GL on a 0-sized surface poisons the
  // viewport until recreate, so every GL op funnels through this check.
  const isLayoutValid = useCallback((): boolean => {
    if (document.hidden) {
      return false;
    }
    if (window.innerWidth <= 0 || window.innerHeight <= 0) {
      return false;
    }
    if (!bounds || bounds.width <= 0 || bounds.height <= 0) {
      return false;
    }
    const canvas = canvasRef.current;
    if (!canvas) {
      return false;
    }
    const rect = canvas.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }, [bounds]);

  // ── Initialise / dispose renderer ───────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    try {
      const renderer = new WindGlRenderer(canvas);
      renderer.onContextRestored = () => {
        reportThrottled(
          'webgl-heatmap:context-restored',
          'WebGLHeatMapLayer: context restored',
          { throttleMs: SENTRY_THROTTLE_MS, level: 'warning', tags: TAGS },
        );
        setRestoreGeneration(c => c + 1);
      };
      rendererRef.current = renderer;
    } catch (error) {
      Sentry.captureMessage('WebGLHeatMapLayer: WebGL not available', {
        level: 'warning',
        tags: TAGS,
        contexts: { webgl: { error: String(error) } },
      });
    }

    return () => {
      cancelAnimationFrame(blendRafRef.current);
      if (stableRecoveryTimerRef.current) {
        clearTimeout(stableRecoveryTimerRef.current);
        stableRecoveryTimerRef.current = 0;
      }
      rendererRef.current?.dispose();
      rendererRef.current = null;
    };
  }, []);

  // ── Apply renderer settings (and keep settingsRef in sync) ──────────────

  useEffect(() => {
    settingsRef.current.theme = theme;
    rendererRef.current?.setTheme(theme);
  }, [theme]);

  useEffect(() => {
    settingsRef.current.opacity = opacity;
    rendererRef.current?.setOpacity(opacity);
  }, [opacity]);

  useEffect(() => {
    settingsRef.current.threshold = threshold;
    rendererRef.current?.setThreshold(threshold);
  }, [threshold]);

  useEffect(() => {
    settingsRef.current.maxWindSpeed = windState.maxWindSpeed;
    if (windState.maxWindSpeed > 0) {
      rendererRef.current?.setMaxWind(windState.maxWindSpeed);
    }
  }, [windState.maxWindSpeed]);

  // ── Sync projection & render ────────────────────────────────────────────

  const syncAndRender = useCallback(() => {
    const renderer = rendererRef.current;
    const projection = projectionRef.current;
    if (!renderer || !projection || renderer.contextLost) {
      return;
    }
    if (!isLayoutValid()) {
      return;
    }

    renderer.setProjection(
      projection.rotate() as [number, number, number],
      projection.scale(),
      projection.translate() as [number, number],
    );
    renderer.render();
  }, [projectionRef, isLayoutValid]);

  // ── Hard renderer recreate (used by debounced and forced recovery) ──────

  const hardRecreateRenderer = useCallback((): boolean => {
    const canvas = canvasRef.current;
    if (!canvas || !bounds || bounds.width <= 0 || bounds.height <= 0) {
      return false;
    }

    rendererRef.current?.dispose();
    rendererRef.current = null;

    let renderer: WindGlRenderer;
    try {
      renderer = new WindGlRenderer(canvas);
    } catch (error) {
      reportThrottled(
        'webgl-heatmap:hard-recreate-failed',
        'WebGLHeatMapLayer: hard recreate failed',
        {
          throttleMs: SENTRY_THROTTLE_MS,
          level: 'error',
          tags: TAGS,
          extra: { error: String(error) },
        },
      );
      return false;
    }

    const s = settingsRef.current;
    renderer.onContextRestored = () => setRestoreGeneration(c => c + 1);
    renderer.setTheme(s.theme);
    renderer.setOpacity(s.opacity);
    renderer.setThreshold(s.threshold);
    if (s.maxWindSpeed > 0) {
      renderer.setMaxWind(s.maxWindSpeed);
    }
    renderer.resize(bounds.width, bounds.height);
    rendererRef.current = renderer;
    setRestoreGeneration(c => c + 1);
    return true;
  }, [bounds]);

  // ── Debounced stable recovery ───────────────────────────────────────────
  // Wait for ~1s of continuously-valid layout before recreating. If layout
  // flips invalid mid-wait, restart the timer.

  const scheduleStableRecovery = useCallback(
    (reason: string) => {
      if (!sawInvalidSizeRef.current) {
        return;
      }
      if (stableRecoveryTimerRef.current) {
        clearTimeout(stableRecoveryTimerRef.current);
      }

      stableRecoveryTimerRef.current = window.setTimeout(() => {
        stableRecoveryTimerRef.current = 0;

        if (!isLayoutValid()) {
          // Recurse via the ref so we always call the latest closure and
          // sidestep the lint rule against self-referencing useCallback.
          scheduleStableRecoveryRef.current?.(reason);
          return;
        }

        Sentry.addBreadcrumb({
          category: 'webgl-heatmap',
          level: 'info',
          message: 'stable recovery',
          data: { reason },
        });

        if (hardRecreateRenderer()) {
          sawInvalidSizeRef.current = false;
        }
      }, STABLE_RECOVERY_DELAY_MS);
    },
    [hardRecreateRenderer, isLayoutValid],
  );

  useEffect(() => {
    scheduleStableRecoveryRef.current = scheduleStableRecovery;
  }, [scheduleStableRecovery]);

  // ── Upload wind data as GPU texture ─────────────────────────────────────

  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || !windState.data || windState.status !== 'success') {
      return;
    }

    const width = windState.width || GRID_WIDTH;
    const height = windState.height || GRID_HEIGHT;
    const fhrIndex = Number(windParams.fhr) || 0;

    let cancelled = false;

    createImageBitmap(windState.data, {
      colorSpaceConversion: 'none',
      premultiplyAlpha: 'none',
    })
      .then(bitmap => {
        if (cancelled) {
          bitmap.close();
          return;
        }

        renderer.uploadWindTextureFromBitmap(fhrIndex, bitmap, width, height);
        bitmap.close();

        const prevFhr = prevFhrRef.current;
        const canBlend =
          preload &&
          prevFhr >= 0 &&
          prevFhr !== fhrIndex &&
          renderer.hasForecastTexture(prevFhr);

        if (canBlend) {
          cancelAnimationFrame(blendRafRef.current);
          const startTime = performance.now();
          const BLEND_DURATION_MS = blendDurationSec * 1000;

          const loop = (now: number) => {
            const t = Math.min((now - startTime) / BLEND_DURATION_MS, 1);
            renderer.setBlend(prevFhr, fhrIndex, t);
            syncAndRender();

            if (t < 1) {
              blendRafRef.current = requestAnimationFrame(loop);
            } else {
              renderer.setForecastHour(fhrIndex);
              blendRafRef.current = 0;
            }
          };
          blendRafRef.current = requestAnimationFrame(loop);
        } else {
          renderer.setForecastHour(fhrIndex);
          syncAndRender();
        }

        prevFhrRef.current = fhrIndex;
      })
      .catch(err => {
        reportThrottled(
          'webgl-heatmap:image-bitmap-failed',
          'WebGLHeatMapLayer: createImageBitmap failed',
          {
            throttleMs: SENTRY_THROTTLE_MS,
            level: 'error',
            tags: TAGS,
            extra: { error: String(err), fhrIndex },
          },
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    windState.data,
    windState.status,
    windState.width,
    windState.height,
    windParams.fhr,
    preload,
    blendDurationSec,
    syncAndRender,
    restoreGeneration,
  ]);

  // ── Resize on bounds change (skip on invalid layout) ────────────────────

  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || !bounds) {
      return;
    }

    if (bounds.width <= 0 || bounds.height <= 0) {
      sawInvalidSizeRef.current = true;
      return;
    }

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) {
      sawInvalidSizeRef.current = true;
      return;
    }

    renderer.resize(bounds.width, bounds.height);
    syncAndRender();
  }, [bounds, syncAndRender]);

  // ── Re-render when projection changes ───────────────────────────────────

  useEffect(() => {
    if (!projectionHash) {
      return;
    }
    syncAndRender();
  }, [projectionHash, syncAndRender]);

  // ── Live pan/zoom rAF loop ──────────────────────────────────────────────

  useEffect(() => {
    const unsubStart = onChangeStart(() => {
      cancelAnimationFrame(rafRef.current);
      const loop = () => {
        syncAndRender();
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    });

    const unsubProgress = onChangeProgress(() => {
      // Handled by the rAF loop.
    });

    const unsubEnd = onChangeEnd(() => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      syncAndRender();
    });

    return () => {
      cancelAnimationFrame(rafRef.current);
      unsubStart();
      unsubProgress();
      unsubEnd();
    };
  }, [contextId, onChangeStart, onChangeProgress, onChangeEnd, syncAndRender]);

  // ── Visibility / resize / focus — schedule recovery if layout misbehaves ──

  useEffect(() => {
    const handle = (reason: string) => {
      if (document.hidden) {
        sawInvalidSizeRef.current = true;
        return;
      }

      const renderer = rendererRef.current;
      if (!renderer) {
        return;
      }

      // Reconcile silently-restored GL context (browser may not fire the event).
      // Only flag invalid if reconcile failed — a successful silent restore
      // doesn't need a debounced hard recreate on top.
      if (renderer.contextLost) {
        if (renderer.reconcileContextState()) {
          setRestoreGeneration(c => c + 1);
        } else {
          sawInvalidSizeRef.current = true;
        }
      }

      if (!isLayoutValid()) {
        sawInvalidSizeRef.current = true;
        scheduleStableRecovery(reason);
        return;
      }

      if (sawInvalidSizeRef.current) {
        scheduleStableRecovery(reason);
        return;
      }

      // Drawing buffer may have been discarded while hidden — repaint.
      syncAndRender();
    };

    const onVis = () => handle('visibilitychange');
    const onResize = () => handle('resize');
    const onFocus = () => handle('focus');

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('resize', onResize);
    window.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('focus', onFocus);
    };
  }, [isLayoutValid, scheduleStableRecovery, syncAndRender]);

  // ── Watchdog: detect persistent blank canvas + escalating recovery ──────
  // Tier 1: schedule debounced recovery on first blank (handled by stableRecovery).
  // Tier 2: after MAX_CONSECUTIVE_BLANKS, force immediate recreate.
  // Tier 3: after MAX_RECREATE_FAILURES, reload the page.
  //
  // Adaptive interval: 1s until we've seen a healthy probe, then 3s to save
  // CPU on the happy path. Drops back to 1s on any blank detection.

  useEffect(() => {
    let hasUploadedOnce = false;
    let consecutiveBlanks = 0;
    let recreateFailures = 0;
    let currentInterval = WATCHDOG_INTERVAL_ACTIVE_MS;
    let timer: number;

    const tick = () => {
      const renderer = rendererRef.current;
      if (!renderer || document.hidden) {
        return;
      }
      if (!isLayoutValid()) {
        sawInvalidSizeRef.current = true;
        return;
      }

      if (!hasUploadedOnce) {
        if (renderer.hasForecastTexture(Number(windParams.fhr) || 0)) {
          hasUploadedOnce = true;
        }
        return;
      }

      if (renderer.contextLost) {
        if (renderer.reconcileContextState()) {
          setRestoreGeneration(c => c + 1);
          return;
        }
        reportThrottled(
          'webgl-heatmap:watchdog-context-lost',
          'WebGLHeatMapLayer watchdog: context lost',
          { throttleMs: SENTRY_THROTTLE_MS, level: 'error', tags: TAGS },
        );
        return;
      }

      if (renderer.probeAlpha(PROBE_GRID_SIZE) === 0) {
        consecutiveBlanks++;
        // Stay at active interval while in a blank streak.
        if (currentInterval !== WATCHDOG_INTERVAL_ACTIVE_MS) {
          currentInterval = WATCHDOG_INTERVAL_ACTIVE_MS;
          clearInterval(timer);
          timer = window.setInterval(tick, currentInterval);
        }

        reportThrottled(
          'webgl-heatmap:watchdog-blank',
          'WebGLHeatMapLayer watchdog: blank canvas',
          {
            throttleMs: SENTRY_THROTTLE_MS,
            level: 'error',
            tags: TAGS,
            extra: {
              consecutiveBlanks,
              recreateFailures,
              contextRestoreCount: renderer.contextRestoreCount,
              forecastTextureCount: renderer.forecastTextureCount,
              activeFhr: windParams.fhr,
            },
          },
        );

        // Tier 2: force recreate ignoring debounce.
        if (consecutiveBlanks >= MAX_CONSECUTIVE_BLANKS) {
          consecutiveBlanks = 0;

          if (hardRecreateRenderer()) {
            sawInvalidSizeRef.current = false;
            return;
          }

          // Recreate failed → escalate.
          recreateFailures++;

          if (recreateFailures >= MAX_RECREATE_FAILURES) {
            // Tier 3: terminal. Reload as last resort.
            reportThrottled(
              'webgl-heatmap:reload',
              'WebGLHeatMapLayer: reloading after exhausting recovery attempts',
              {
                throttleMs: SENTRY_THROTTLE_MS,
                level: 'fatal',
                tags: TAGS,
                extra: { recreateFailures },
              },
            );
            window.location.reload();
            return;
          }
          return;
        }

        // Tier 1: debounced recovery for the first blanks.
        sawInvalidSizeRef.current = true;
        scheduleStableRecovery('watchdog-blank');
        return;
      }

      // Healthy probe — reset streak counters and slow watchdog down.
      if (consecutiveBlanks > 0 || recreateFailures > 0) {
        consecutiveBlanks = 0;
        recreateFailures = 0;
      }
      if (currentInterval !== WATCHDOG_INTERVAL_IDLE_MS) {
        currentInterval = WATCHDOG_INTERVAL_IDLE_MS;
        clearInterval(timer);
        timer = window.setInterval(tick, currentInterval);
      }
    };

    timer = window.setInterval(tick, currentInterval);

    return () => clearInterval(timer);
  }, [
    windParams.fhr,
    isLayoutValid,
    scheduleStableRecovery,
    hardRecreateRenderer,
  ]);

  if (!bounds) {
    return null;
  }

  return (
    <Root>
      <StyledCanvas ref={canvasRef} />
    </Root>
  );
};
